import {
  useGenerateSalarySheetMutation,
  useGetAllPayrollsByBatchQuery,
  useGetSinglePayrollBatchQuery,
  type PayrollBatchRow,
} from '@/app/features/employee/emp-payroll';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import useDiscloser from '@/hooks/useDiscloser';
import useFilters from '@/hooks/useFilters';
import { EyeIcon, LockIcon, PencilEdit02Icon, UploadIcon } from '@/icons';
import DashboardLayout from '@/layouts/DashboardLayout';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import type { StatusOption } from '@/types/global.types';
import { formatNumber } from '@/utils';
import { Button, Space, Tooltip, type TableProps } from 'antd';
import { useState } from 'react';
import { useParams } from 'react-router';
import PayrollPageHeader from '../shared/PayrollPageHeader';
import SearchInput from '../shared/SearchInput';
import { PAYROLL_PATH } from '../shared/payroll.constants';
import { formatBDT } from '../shared/payroll.helpers';
import AdjustmentDrawer from './AdjustmentDrawer';
import AdjustmentUpload from './AdjustmentUpload';
import SalaryBreakdownDrawer from './SalaryBreakdownDrawer';
import {
  dailyPayOf,
  isSheetGenerated,
  rowAmounts,
} from './batch-payroll.helpers';

const SHEET_STATE_CONFIG: StatusOption[] = [
  {
    label: 'Generated',
    value: 'GENERATED',
    className: 'status status-success',
  },
  {
    label: 'Not Generated',
    value: 'NOT_GENERATED',
    className: 'status status-slate',
  },
];

const LOCKED_HINT = 'Salary sheet generated. Amounts are final.';

/** An amount cell. Zero is dimmed so real adjustments stand out. */
const Amount = ({ value }: { value: number }) => (
  <span className={value ? 'text-white' : 'text-white/40'}>
    {formatBDT(value)}
  </span>
);

/**
 * One payroll batch: every employee row with its pay.
 *
 * Amounts are adjusted here, one row at a time in a drawer or all at once by
 * Excel. Generating a row's salary sheet then locks its amounts for good.
 */
export default function BatchPayrolls() {
  const { id = '' } = useParams();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openUpload, setOpenUpload] = useState(false);
  /** The row open in the adjustment editor or in the breakdown view. */
  const [drawer, openDrawer, closeDrawer] = useDiscloser<
    'ADJUST' | 'BREAKDOWN',
    PayrollBatchRow
  >();

  const { page, limit, keyword, handleSearch, handlePagination } = useFilters();

  const { data, isLoading, isFetching } = useGetAllPayrollsByBatchQuery(
    { id, args: { params: { page, limit, search: keyword } } },
    { skip: !id },
  );
  const rows = data?.data?.data || [];

  // The row list is paginated, so "everyone is generated" is read from the
  // batch itself, whose history holds every row.
  const { data: batchData } = useGetSinglePayrollBatchQuery(id, { skip: !id });
  const batch = batchData?.data;
  const history = batch?.history || [];
  const allGenerated = history.length
    ? history.every(isSheetGenerated)
    : isSheetGenerated(batch);

  const [generateSheets, { isLoading: generating }] =
    useGenerateSalarySheetMutation();

  /** Generated rows are final, so their editor never opens. */
  const handleAdjust = (row: PayrollBatchRow) => {
    if (!isSheetGenerated(row)) openDrawer({ type: 'ADJUST', data: row });
  };

  const handleOpenUpload = () => {
    if (!allGenerated) setOpenUpload(true);
  };

  const handleGenerate = async () => {
    if (!selectedIds.length) return;
    try {
      await generateSheets({ ids: selectedIds }).unwrap();
      toast('success', 'Salary sheet generated successfully');
      setSelectedIds([]);
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to generate salary sheet'));
    }
  };

  // Daily batches add a days × rate column; a monthly batch renders as before.
  const hasDailyRows = rows.some((row) => dailyPayOf(row));

  const columns: TableProps<PayrollBatchRow>['columns'] = [
    {
      title: 'Department',
      dataIndex: 'department_name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Employee ID',
      dataIndex: 'employee_id',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Employee Name',
      dataIndex: 'employee_name',
      render: (val: string) => val || 'n/a',
    },
    ...(hasDailyRows
      ? [
          {
            title: 'Payable Days',
            align: 'center' as const,
            render: (_: unknown, row: PayrollBatchRow) => {
              const daily = dailyPayOf(row);
              if (!daily) return 'n/a';
              return (
                <span className="text-white whitespace-nowrap">
                  {daily.payableDays}{' '}
                  <span className="text-white/40">
                    × {formatNumber(daily.dailyRate)}
                  </span>
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: 'Gross Salary',
      align: 'center',
      render: (_, row) => formatBDT(rowAmounts(row).gross),
    },
    {
      title: 'Addition',
      align: 'center',
      render: (_, row) => <Amount value={rowAmounts(row).addition} />,
    },
    {
      title: 'Deduction',
      align: 'center',
      render: (_, row) => <Amount value={rowAmounts(row).deduction} />,
    },
    {
      title: 'Tax',
      align: 'center',
      render: (_, row) => <Amount value={rowAmounts(row).tax} />,
    },
    {
      title: 'Final Salary',
      dataIndex: 'net_salary',
      align: 'center',
      render: (val: string) => (
        <span className="text-theme-primary font-semibold whitespace-nowrap">
          {formatBDT(val)}
        </span>
      ),
    },
    {
      title: 'Salary Sheet',
      dataIndex: 'salary_sheet',
      align: 'center',
      render: (val: string) => (
        <StatusTag
          value={val}
          config={SHEET_STATE_CONFIG}
          shouldCapitalize={false}
        />
      ),
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, row) => (
        <Space>
          {isSheetGenerated(row) ? (
            <Tooltip title={LOCKED_HINT}>
              <span className="inline-flex p-1 text-white/40">
                <LockIcon size={20} />
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Edit adjustments">
              <Button
                size="small"
                className="action-btn"
                icon={<PencilEdit02Icon size={20} />}
                onClick={() => handleAdjust(row)}
              />
            </Tooltip>
          )}
          <Tooltip title="View breakdown">
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() => openDrawer({ type: 'BREAKDOWN', data: row })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout title="Employee">
      <PayrollPageHeader
        crumbs={[
          { title: 'Payroll & Salary Sheet Generate', path: PAYROLL_PATH },
          { title: 'Batch' },
        ]}
      >
        <SearchInput placeholder="Search Employee ID" onChange={handleSearch} />

        <Tooltip
          title={
            allGenerated
              ? 'Salary sheet is generated for everyone in this batch'
              : undefined
          }
        >
          <Button
            size="large"
            icon={<UploadIcon />}
            className="btn-primary"
            disabled={!id || allGenerated}
            onClick={handleOpenUpload}
          >
            Upload Adjustments
          </Button>
        </Tooltip>
      </PayrollPageHeader>

      <div className="mt-6">
        <CustomTable<PayrollBatchRow>
          page={page}
          limit={limit}
          hideRowSelect
          columns={columns}
          dataSource={rows}
          onPaginate={handlePagination}
          tableRootClassName="!bg-transparent !border-none !p-0"
          customTitle={batch?.batch_name || 'Batch'}
          loading={isLoading || isFetching}
          total={data?.data?.meta?.total || 0}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as string[]),
            getCheckboxProps: (row) => ({
              disabled: isSheetGenerated(row),
              className: 'custom-check',
            }),
            columnWidth: 40,
          }}
          filterSection={
            <Button
              size="middle"
              loading={generating}
              disabled={!selectedIds.length || generating}
              onClick={handleGenerate}
              className="btn-primary !text-theme-gray-650 !font-bold hover:!text-theme-gray-650 !rounded-md"
            >
              Generate Salary Sheet
              {selectedIds.length ? ` (${selectedIds.length})` : ''}
            </Button>
          }
        />
      </div>

      <AdjustmentUpload
        open={openUpload && !allGenerated}
        onClose={() => setOpenUpload(false)}
        batchId={id}
      />

      <AdjustmentDrawer
        open={
          drawer.open &&
          drawer.type === 'ADJUST' &&
          !isSheetGenerated(drawer.data)
        }
        row={drawer.data}
        onClose={() => closeDrawer()}
      />

      <SalaryBreakdownDrawer
        open={drawer.open && drawer.type === 'BREAKDOWN'}
        row={drawer.data}
        onClose={() => closeDrawer()}
      />
    </DashboardLayout>
  );
}
